import React, { useEffect, useState } from "react";
import ModalShell from "./ModalShell";
import { validateEmail } from "../shared/utils/validation";
const { displayEmailOrDash } = require("../shared/utils/displayEmail");

/**
 * Capture a new login email for User Admin row action "Swap email".
 *
 * @param {boolean} open
 * @param {object|null} user
 * @param {() => void} onCancel
 * @param {(newEmail: string) => Promise<void>} onSubmit
 */
function UserEmailSwapModal({ open, user, onCancel, onSubmit }) {
  const [newEmail, setNewEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setNewEmail("");
    setSubmitting(false);
    setError("");
  }, [open, user]);

  const currentEmail = user && user.email_id != null ? String(user.email_id).trim() : "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!onSubmit || submitting) return;

    const emailCheck = validateEmail(newEmail);
    if (!emailCheck.valid) {
      setError(emailCheck.message || "Invalid email");
      return;
    }
    const next = String(newEmail || "").trim().toLowerCase();
    if (next === currentEmail.toLowerCase()) {
      setError("New email is the same as the current email");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await onSubmit(String(newEmail || "").trim());
    } catch (err) {
      const msg = err && typeof err.message === "string" ? err.message.trim() : "";
      if (msg) setError(msg);
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  return (
    <ModalShell
      open={open}
      onClose={submitting ? undefined : onCancel}
      title="Swap email"
      subtitle="Updates the login email in the database and Firebase, then sends a welcome email to the new address."
      maxWidth={480}
      zIndex={1090}
      closeOnOverlayClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={submitting}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="user-email-swap-form"
            className="btn btn-primary"
            disabled={submitting || !String(newEmail || "").trim()}
          >
            {submitting ? "Updating…" : "Swap email"}
          </button>
        </>
      }
    >
      <form id="user-email-swap-form" className="modal-form" onSubmit={handleSubmit}>
        {error ? (
          <div style={{ color: "#c92a2a", fontSize: 13, marginBottom: 12 }}>{error}</div>
        ) : null}
        <div className="form-field">
          <label htmlFor="user-email-swap-current">Current email</label>
          <input
            id="user-email-swap-current"
            type="text"
            className="input"
            value={displayEmailOrDash(currentEmail)}
            disabled
            readOnly
          />
        </div>
        <div className="form-field">
          <label htmlFor="user-email-swap-new">New email *</label>
          <input
            id="user-email-swap-new"
            type="email"
            className="input"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            disabled={submitting}
            autoComplete="email"
            autoFocus
          />
        </div>
      </form>
    </ModalShell>
  );
}

export default UserEmailSwapModal;
